import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { getKols, createKol, updateKol, deleteKol } from '../../services/kolService';


export interface Kol {
  id: string;
  name: string;
  email: string;
  phone: string;
  instagramHandle: string;
  instagramFollowers: number;
  tiktokHandle: string;
  tiktokFollowers: number;
  categories: string[];
  location: string;
  notes?: string;
  bio?: string
  createdAt: string;
  updatedAt: string;
}

interface KolState {
  kols: Kol[];
  selectedKol: Kol | null;
  isLoading: boolean;
  error: string | null;
  filters: {
    search: string;
    category: string;
    minInstagramFollowers: number;
    minTiktokFollowers: number;
  };
}

const initialState: KolState = {
  kols: [],
  selectedKol: null,
  isLoading: false,
  error: null,
  filters: {
    search: '',
    category: '',
    minInstagramFollowers: 0,
    minTiktokFollowers: 0,
  },
};

export const fetchKols = createAsyncThunk('kol/fetchKols', async (_, { rejectWithValue }) => {
  try {
    const kols = await getKols();
    return kols;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch KOLs');
  }
});


export const addKol = createAsyncThunk('kol/addKol', async (newKol: Omit<Kol, 'id' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
  try {
    const response = await createKol(newKol);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to add KOL');
  }
});

export const editKol = createAsyncThunk('kol/editKol', async ({ id, data }: { id: string; data: Partial<Kol> }, { rejectWithValue }) => {
  try {
    const response = await updateKol(id, data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update KOL');
  }
});

export const removeKol = createAsyncThunk('kol/removeKol', async (id: string, { rejectWithValue }) => {
  try {
    await deleteKol(id);
    return id;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete KOL');
  }
});

const kolSlice = createSlice({
  name: 'kol',
  initialState,
  reducers: {
    setSelectedKol: (state, action: PayloadAction<Kol | null>) => {
      state.selectedKol = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<KolState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchKols.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchKols.fulfilled, (state, action) => {
        state.isLoading = false;
        state.kols = action.payload;
      })
      .addCase(fetchKols.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        message.error(action.payload as string);
      })
      .addCase(addKol.fulfilled, (state, action) => {
        state.kols.push(action.payload);
        message.success('KOL added successfully');
      })
      .addCase(addKol.rejected, (state, action) => {
        state.error = action.payload as string;
        message.error(action.payload as string);
      })
      .addCase(editKol.fulfilled, (state, action) => {
        const index = state.kols.findIndex(kol => kol.id === action.payload.id);
        if (index !== -1) {
          state.kols[index] = action.payload;
        }
        message.success('KOL updated successfully');
      })
      .addCase(editKol.rejected, (state, action) => {
        state.error = action.payload as string;
        message.error(action.payload as string);
      })
      .addCase(removeKol.fulfilled, (state, action) => {
        state.kols = state.kols.filter(kol => kol.id !== action.payload);
        message.success('KOL deleted successfully');
      })
      .addCase(removeKol.rejected, (state, action) => {
        state.error = action.payload as string;
        message.error(action.payload as string);
      });
  },
});

export const { setSelectedKol, setFilters, clearFilters } = kolSlice.actions;
export default kolSlice.reducer;